/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/anchor-has-content */
import * as React from 'react';
import { observer } from 'mobx-react';
import * as IShareButtonsProps from './IShareButtonsProps';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Injector } from 'Core';
import { IReportDataService } from 'Services';

/**
 * ShareButtons Component
 *
 * This component provides social sharing functionality for reports. It handles:
 * - Print, email, Facebook/Twitter sharing, and clipboard copy
 *
 * Flow:
 * 1. Component receives the full URL through props.shareLink from the parent Report component
 * 2. When a share button is clicked, it:
 *    a. Generates a shortened URL using the ReportDataService
 *    b. Updates the parent component's state using setShareLink
 *    c. Executes the specific sharing callback (e.g., shareOnFacebookOnClick)
 *
 * Note: URL shortening is performed only when a share button is clicked, not on initial render,
 * to optimize performance and avoid unnecessary API calls.
 */
@observer
export class ShareButtons extends React.Component<
  IShareButtonsProps.IShareButtonsProps,
  {
    /**
     * @description The share link before it was shortened.
     * We store it to prevent calling the API for the same link multiple times.
     */
    previousShareLink: string;
    isGeneratingShortUrl: boolean;
  }
> {
  private reportDataService: IReportDataService;

  constructor(props: IShareButtonsProps.IShareButtonsProps) {
    super(props, undefined);
    this.state = {
      previousShareLink: '',
      isGeneratingShortUrl: false,
    };
  }

  generateShortUrl = (callBack?: VoidFunction) => {
    try {
      if (this.state.previousShareLink === this.props.shareLink) {
        callBack?.();
        return;
      }
      this.reportDataService = Injector.resolve<IReportDataService>('IReportDataService');
      if (!this.reportDataService) {
        throw new Error('ReportDataService is not registered');
      }
      this.setState({ isGeneratingShortUrl: true });
      this.reportDataService
        .shortenUrl(this.props.shareLink)
        .then(shortUrl => {
          this.setState({ isGeneratingShortUrl: false, previousShareLink: shortUrl });
          this.props.setShareLink(shortUrl);
        })
        .then(() => callBack?.());
    } catch (error) {
      console.error(error);
      this.setState({ isGeneratingShortUrl: false });
    }
  };

  render() {
    return (
      <div className="share-container ShareButtons">
        <p>Share This</p>
        <a id="copyToClipboard"></a>
        <div className={`share-icons-wrapper ${this.state.isGeneratingShortUrl ? 'loading' : ''}`}>
          <div className="icon" data-toggle="modal" data-target="#print-recommendation">
            <img src="Images/print-icon.svg" alt="Print" />
          </div>
          <div className="icon mail-icon">
            <a onClick={() => this.generateShortUrl(this.props.onClickMailButton)}>
              <img src="Images/mail-icon-all-new.png" alt="E-mail" />
            </a>
          </div>
          <div
            className="icon"
            onClick={() => this.generateShortUrl(this.props.shareOnFacebookOnClick)}
          >
            <img src="Images/facebook-icon.svg" alt="Facebook" />
          </div>
          <div
            className="icon"
            onClick={() => this.generateShortUrl(this.props.shareOnTwitterOnClick)}
          >
            <a id="share-on-twitter" target="_blank">
              <img src="Images/twitter-icon.svg" alt="Twitter" />
            </a>
          </div>
          <div
            className="icon"
            id="copy-to-clipboard-btn"
            data-target="#copy-to-clipboard"
            data-toggle="modal"
            onClick={() => this.generateShortUrl()}
          >
            <img src="Images/copy-to-clipboard.svg" alt="Copy link to the clipboard" />
          </div>
        </div>
        {this.renderPrintRecommendationModal()}
        {this.renderCopyToClipboardModal()}
      </div>
    );
  }

  renderPrintRecommendationModal() {
    return (
      <div
        className="modal fade"
        tabIndex={-1}
        id="print-recommendation"
        role="dialog"
        aria-labelledby="printRecommendation"
      >
        <div className="modal-dialog modal-xs" role="document">
          <div className="modal-content">
            <div className="modal-body">
              <img
                src="Images/MultiGroupReport/Close_icon.svg"
                alt=""
                className="close-btn"
                data-dismiss="modal"
                aria-label="Close"
              />
              <div className="print-recommendation-text">
                The report looks the best when printed in the landscape orientation.
              </div>
              <div className="print-recommendation-buttons">
                <a
                  className="ok-btn"
                  target="_blank"
                  href={this.props.reportPrintUrl}
                  rel="noopener noreferrer"
                >
                  <span>OK</span>
                </a>
                <div className="cancel-btn" data-dismiss="modal">
                  <span>Cancel</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderCopyToClipboardModal() {
    return (
      <div
        className="modal fade"
        tabIndex={-1}
        id="copy-to-clipboard"
        role="dialog"
        aria-labelledby="printRecommendation"
      >
        <div className="modal-dialog modal-xs" role="document">
          <div className="modal-content">
            <div className="modal-body">
              <img
                src="Images/MultiGroupReport/Close_icon.svg"
                alt=""
                className="close-btn"
                data-dismiss="modal"
                aria-label="Close"
              />
              <div className="text-and-button-wrapper">
                <div className="copied-to-clipboard-text">
                  {this.state.isGeneratingShortUrl
                    ? 'Generating short URL...'
                    : 'Press the OK button to copy the report link to the clipboard.'}
                </div>
                <CopyToClipboard
                  text={this.props.shareLink}
                  className="ok-btn"
                  data-dismiss="modal"
                  disabled={this.state.isGeneratingShortUrl}
                >
                  <span>OK</span>
                </CopyToClipboard>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
